import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { User } from 'app/classes/user';
import { Group } from 'app/group-management/models/group.model';
import { environment } from 'environments/environment';
import { PlanMetadata } from 'fitforce-document-sync';
import * as moment from 'moment';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { FitnessSurveyData } from '../models/fitness-survey-data.model';
import { GroupTrainingLoad } from '../models/group-training-load.model';
import { PftCftObservationDocument } from '../models/individual-pft-cft-score.model';
import { IndividualTrainingLoad } from '../models/individual-training-load.model';
import { PlanTrainingLoad } from '../models/plan-training-load.model';
import { TrainingLoadAnalysis } from '../models/training-load-analysis.model';
import { TrainingLoadParams, TrainingLoadType } from '../models/training-load-params.model';

function convertToIndividualTrainingLoad(response: any) {
  return {
    date: moment(response.date).toDate(),
    workoutName: response.workout_name,
    groupUUID: response.group_uuid,
    groupName: response.group_name,
    isComplete: response.is_complete,
    actualTrainingLoad: response.actual_training_load,
    estTrainingLoad: response.estimated_training_load,
  } as IndividualTrainingLoad;
}

function convertToTrainingLoadAnalysis(response: any) {
  return {
    weekNumber: response.week_number,
    meanEstTrainingLoad: response.mean_est_training_load,
    sdEstTrainingLoad: response.sd_est_training_load,
    meanActualTrainingLoad: response.mean_actual_training_load,
    sdActualTrainingLoad: response.sd_actual_training_load,
  } as TrainingLoadAnalysis;
}

function convertToPlanTrainingLoad(response: any) {
  return {
    date: moment(response.date).toDate(),
    workoutName: response.workout_name,
    phase: response.phase,
    workoutExecution: response.workout_execution,
    meanEstTrainingLoad: response.mean_estimated_training_load,
    meanActualTrainingLoad: response.mean_actual_training_load,
    sdEstTrainingLoad: response.sd_est_training_load,
    sdActualTrainingLoad: response.sd_actual_training_load,
  } as PlanTrainingLoad;
}

function convertToGroupTrainingLoad(response: any) {
  return {
    date: moment(response.date).toDate(),
    workoutName: response.workout_name,
    planName: response.plan_name,
    phase: response.phase,
    workoutExecution: response.workout_execution,
    meanActualTrainingLoad: response.mean_actual_training_load,
    meanEstTrainingLoad: response.mean_estimated_training_load,
    sdActualTrainingLoad: response.sd_actual_training_load,
    sdEstTrainingLoad: response.sd_est_training_load,
  } as GroupTrainingLoad;
}

function convertToGroup(groupData: any) {
  const returnGroup = new Group();

  returnGroup.uuid = groupData.uuid;
  returnGroup.name = groupData.name;
  returnGroup.privateGroup = groupData.private;
  returnGroup.owners = groupData.owners.map(u => ({
    uuid: u.uuid,
    firstName: u.firstname,
    lastName: u.lastname,
    rank: u.rank,
  }));
  returnGroup.members = groupData.members.map(u => ({
    uuid: u.uuid,
    firstName: u.firstname,
    lastName: u.lastname,
    rank: u.rank,
    email: u.email,
  }));
  // returnGroup.requestedMembers = groupData.requested_members ? groupData.requested_members.map(u => ({ uuid: u.id })) : [];

  if (groupData.created_on_timestamp) {
    returnGroup.createdAt = moment(groupData.created_on_timestamp);
  } else {
    returnGroup.createdAt = moment();
  }

  return returnGroup;
}

function convertToUser(userData: any): User {
  const obj = {
    uuid: userData.uuid,
    firstName: userData.firstname,
    lastName: userData.lastname,
    rank: userData.rank,
    email: userData.email,
    invited: userData.invited,
    confirmed: userData.confirmed,
    email_domain_approved: userData.email_domain_approved,
    hasProfilePic: userData.image_name !== null, // TODO: confirm this works
    profilePicture: 'assets/images/no-image.png',
    joinedDate: userData.registration_timestamp as Date,
  } as User;
  return obj;
}

function convertToTrainingLoadParams(paramData: any): TrainingLoadParams {
  return {
    startDate: paramData.start_date as moment.Moment,
    endDate: paramData.end_date as moment.Moment,
  } as TrainingLoadParams;
}

function convertToPlanMetadata(data: any): PlanMetadata {
  const obj = new PlanMetadata(data);
  obj.userUUID = data.createdByUser.userUUID;
  return obj;
}

function convertToFitnessSurveyAggregateData(data: any): FitnessSurveyData {
  const fitnessSurveyData = <FitnessSurveyData>{
    start_date: data.start_date as moment.Moment,
    end_date: data.end_date as moment.Moment,
    scores_avg: data.scores_avg,
    total_survey_responses: data.total_survey_responses,
  };
  return fitnessSurveyData;
}

function convertAllFitnessSurveyAggregateData(data: any[]): FitnessSurveyData[] {
  const surveyData = [];
  if (!data || data.length === 0) {
    return surveyData;
  }
  for (let i = 0; i < data.length; ++i) {
    const dataSegment = data[i];
    const surveyDataSegment = convertToFitnessSurveyAggregateData(dataSegment);
    if (surveyDataSegment) {
      surveyData.push(surveyDataSegment);
    }
  }
  return surveyData;
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  constructor(private http: HttpClient, private snackbar: MatSnackBar, private sanitizer: DomSanitizer) {}

  handleError(e: any): Observable<never> {
    if (this.snackbar && e) {
      this.snackbar.open('Error: ' + e.message, 'Dismiss');
      return throwError(e.message || 'Server error');
    }
  }

  getTotalFitnessSurveyAggregateData(startDate: Date, endDate: Date): Observable<FitnessSurveyData[]> {
    const formattedStartDate = moment(startDate).startOf('week').format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    const jwt = 'Bearer ' + localStorage.getItem(AuthService.authTokenKey);
    const route = `/SurveyResponseAnalytics/aggregate-scores?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
    const headers = new HttpHeaders({ Authorization: `${jwt}` });
    return this.http.get(this.buildRoute(route), { headers: headers }).pipe(
      catchError(e => this.handleError(e)),
      map((response: any[]) => convertAllFitnessSurveyAggregateData(response)),
    );
  }

  getGroups(): Observable<Group[]> {
    return this.get('/Group/?onlyOwned=true').pipe(
      map(
        (response: any) => response.map(convertToGroup),
        catchError(e => this.handleError(e)),
      ),
    );
  }

  getGroupTrainingLoads(groupUUID: string, startDate: Date, endDate: Date): Observable<GroupTrainingLoad[]> {
    const formattedStartDate = moment(startDate).startOf('week').format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    return this.get(
      `/TrainingLoadAnalytics/training-load/group/${groupUUID}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
    ).pipe(
      catchError(this.handleError),
      map((response: any) => response.training_loads.map(res => convertToGroupTrainingLoad(res))),
    );
  }

  getGroupTrainingLoadAnalyses(groupUUID: string, startDate: Date, endDate: Date): Observable<TrainingLoadAnalysis[]> {
    const formattedStartDate = moment(startDate).startOf('week').format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    return this.get(
      `/TrainingLoadAnalytics/training-load/group/${groupUUID}/analysis?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
    ).pipe(
      map((response: any) => response.training_loads.map(res => convertToTrainingLoadAnalysis(res))),
      catchError(err => this.handleError(err)),
    );
  }

  getIndividualTrainingLoads(marineUUID: string, startDate: Date, endDate: Date): Observable<IndividualTrainingLoad[]> {
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    return this.get(
      `/TrainingLoadAnalytics/training-load/individual/${marineUUID}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
    ).pipe(
      map((response: any) => response.training_loads.map(res => convertToIndividualTrainingLoad(res))),
      catchError(err => this.handleError(err)),
    );
  }

  getIndividualTrainingLoadAnalyses(marineUUID: string, startDate: Date, endDate: Date): Observable<TrainingLoadAnalysis[]> {
    const formattedStartDate = moment(startDate).startOf('week').format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    return this.get(
      `/TrainingLoadAnalytics/training-load/individual/${marineUUID}/analysis?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
    ).pipe(
      map((response: any) => response.training_loads.map(res => convertToTrainingLoadAnalysis(res))),
      catchError(err => this.handleError(err)),
    );
  }

  getPlanTrainingLoads(planUUID: string, startDate: Date, endDate: Date): Observable<PlanTrainingLoad[]> {
    const formattedStartDate = moment(startDate).startOf('week').format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    return this.get(
      `/TrainingLoadAnalytics/training-load/plan/${planUUID}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
    ).pipe(
      map((response: any) => response.training_loads.map(res => convertToPlanTrainingLoad(res))),
      catchError(err => this.handleError(err)),
    );
  }

  getPlanTrainingLoadAnalyses(planUUID: string, startDate: Date, endDate: Date): Observable<TrainingLoadAnalysis[]> {
    const formattedStartDate = moment(startDate).startOf('week').format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    return this.get(
      `/TrainingLoadAnalytics/training-load/plan/${planUUID}/analysis?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
    ).pipe(
      map((response: any) => response.training_loads.map(res => convertToTrainingLoadAnalysis(res))),
      catchError(err => this.handleError(err)),
    );
  }

  getPlansMetadata(groupUUID: string): Observable<PlanMetadata[]> {
    return this.get(`/Group/getPlanDocumentsPublishedToGroup/${groupUUID}`).pipe(
      map((response: any) => {
        return response.plans.map(res => convertToPlanMetadata(res));
        // return response.plans.map(res => new PlanMetadata(res));
      }),
      catchError(err => this.handleError(err)),
    );
  }

  getUserData(marineUUID: string) {
    return this.get(`/User/${marineUUID}`).pipe(
      map((response: any) => {
        return convertToUser(response);
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  getUserImage(userUUID: string) {
    return this.get(`/User/${userUUID}/image`).pipe(
      map((data: any) => {
        return this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(data));
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  getTrainingLoadParams(UUID: string, type: TrainingLoadType) {
    return this.get(`/TrainingLoadAnalytics/training-load/${type}/${UUID}/analysis/params`).pipe(
      map((response: any) => {
        return convertToTrainingLoadParams(response);
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  getIndividualPftDocuments(userUuid: string) {
    return this.get(`/ObservationDocument/pft/user/${userUuid}`).pipe(
      concatMap((response: any) => {
        const obs = response.documents.map(docUuid => {
          return this.get(`/ObservationDocument/pft/${docUuid}`).pipe(
            map((_response: any) => {
              return { document: JSON.parse(_response.document) as PftCftObservationDocument };
            }),
          );
        });
        return forkJoin(obs);
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  getGroupPftDocuments(groupUuid: string) {
    return this.get(`/ObservationDocument/pft/group/${groupUuid}`).pipe(
      concatMap((response: any) => {
        const obs = response.documents.map(docUuid => {
          return this.get(`/ObservationDocument/pft/${docUuid}`).pipe(
            map((_response: any) => {
              return { document: JSON.parse(_response.document) as PftCftObservationDocument };
            }),
          );
        });
        return forkJoin(obs);
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  getIndividualCftDocuments(userUuid: string) {
    return this.get(`/ObservationDocument/cft/user/${userUuid}`).pipe(
      concatMap((response: any) => {
        const obs = response.documents.map(docUuid => {
          return this.get(`/ObservationDocument/cft/${docUuid}`).pipe(
            map((_response: any) => {
              return { document: JSON.parse(_response.document) as PftCftObservationDocument };
            }),
          );
        });
        return forkJoin(obs);
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  getGroupCftDocuments(groupUuid: string) {
    return this.get(`/ObservationDocument/cft/group/${groupUuid}`).pipe(
      concatMap((response: any) => {
        const obs = response.documents.map(docUuid => {
          return this.get(`/ObservationDocument/cft/${docUuid}`).pipe(
            map((_response: any) => {
              return { document: JSON.parse(_response.document) as PftCftObservationDocument };
            }),
          );
        });
        return forkJoin(obs);
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  private get(route: string) {
    return this.http.get(this.buildRoute(route));
  }

  private buildRoute(route: string) {
    return environment.applySubstitutions(environment.api_url) + 'api' + route;
  }
}
